
import React, { useEffect, useState } from 'react';
import { 
  addDays, 
  format, 
  startOfWeek, 
  getDate, 
  isToday 
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { Play, Youtube } from 'lucide-react';
import { VideoEvent } from '@/hooks/useVideoEvents';
import { useMediaQuery } from '@/hooks/use-mobile';
import { fetchProfileName } from '@/components/dashboard/profiles/content/videos/services/profileService';
import VideoDetailsDialog from '../components/VideoDetailsDialog';

interface WeekViewProps {
  currentDate: Date;
  videoEvents: VideoEvent[];
  onVideoClick: (videoId?: string) => void;
}

const WeekView: React.FC<WeekViewProps> = ({ 
  currentDate, 
  videoEvents,
  onVideoClick
}) => {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [channelNames, setChannelNames] = useState<Record<string, string>>({});
  const [selectedVideo, setSelectedVideo] = useState<VideoEvent | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Get start of week (Monday)
  const startWeek = startOfWeek(currentDate, { weekStartsOn: 1 });
  
  // Get day names for the week
  const dayNames = Array.from({ length: 7 }).map((_, i) => {
    const day = addDays(startWeek, i);
    return format(day, isMobile ? 'EEE dd' : 'EEEE dd', { locale: fr });
  });
  
  // Get dates for the current week
  const weekDates = Array.from({ length: 7 }).map((_, i) => {
    const date = addDays(startWeek, i);
    return date;
  });

  const handleVideoClick = (event: VideoEvent, e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setSelectedVideo(event);
    setIsDialogOpen(true);
    // Still call onVideoClick for any tracking purposes, but without navigating
    if (onVideoClick) onVideoClick(event.videoId);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedVideo(null);
  };

  // Fetch channel names for all profile IDs
  useEffect(() => {
    const fetchChannelNames = async () => {
      const profileIds = [...new Set(videoEvents.map(event => event.profileId))];
      const results: Record<string, string> = {};
      
      for (const profileId of profileIds) {
        try {
          const name = await fetchProfileName(profileId);
          results[profileId] = name;
        } catch (error) {
          console.error(`Error fetching name for profile ${profileId}:`, error);
          results[profileId] = "Chaîne inconnue";
        }
      }
      
      setChannelNames(results);
    };
    
    if (videoEvents.length > 0) {
      fetchChannelNames();
    }
  }, [videoEvents]);

  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    return videoEvents.filter(event => 
      date.getDate() === event.date.getDate() && 
      date.getMonth() === event.date.getMonth() && 
      date.getFullYear() === event.date.getFullYear()
    );
  };

  return (
    <>
      <div className="grid grid-cols-7 border-b dark:border-gray-800/50">
        {dayNames.map((day, index) => (
          <div 
            key={index} 
            className="text-center p-2 font-medium border-r last:border-r-0 capitalize dark:border-gray-800/50 dark:text-gray-300"
          >
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 h-full" style={{ minHeight: "calc(100vh - 230px)" }}>
        {weekDates.map((date, index) => {
          const dayEvents = getEventsForDate(date);
          
          return (
            <div 
              key={index} 
              className={`border-r last:border-r-0 border-b p-2 overflow-y-auto dark:border-gray-800/50 ${
                isToday(date) ? 'bg-gray-50 dark:bg-[#1F2233]' : 'dark:bg-[#121624]'
              }`}
            >
              {/* Display video events for this day */}
              {dayEvents.map(event => (
                <div 
                  key={event.id}
                  onClick={(e) => handleVideoClick(event, e)}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 overflow-hidden cursor-pointer hover:shadow-md transition-shadow dark:bg-[#252A41] dark:border-gray-700/50"
                >
                  {/* Channel name and time header */}
                  <div className="flex items-center justify-between px-2 py-1 bg-white dark:bg-[#2D3348] dark:text-gray-300 dark:border-b dark:border-gray-700/50">
                    <div className="flex items-center">
                      <Youtube className="h-4 w-4 text-red-600 mr-1" />
                      <span className="text-xs font-medium truncate max-w-[100px]">
                        {channelNames[event.profileId] || "..."}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{event.time}</span>
                  </div>

                  {/* Video thumbnail */}
                  {event.videoId && (
                    <div className="relative w-full aspect-video bg-gray-200 dark:bg-gray-800">
                      <img 
                        src={`https://img.youtube.com/vi/${event.videoId}/mqdefault.jpg`} 
                        alt={event.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'https://placehold.co/600x400?text=No+Thumbnail';
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full bg-red-600 bg-opacity-80 flex items-center justify-center">
                          <Play className="h-4 w-4 text-white ml-0.5" />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Optional: Video title tooltip on hover */}
                  <div className="hidden group-hover:block absolute bg-black bg-opacity-75 text-white text-xs p-1 rounded bottom-0 left-0 right-0">
                    {event.title}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <VideoDetailsDialog 
        video={selectedVideo}
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
      />
    </>
  );
};

export default WeekView;
